import React from 'react';
import {
  calcEstimatedUnits,
  undoCPM
} from '@/features/line-items/line-item-form/utils/line-item-form-utils';
import { isNil } from 'lodash';
import { formatCurrency } from '@/components/tables/table-utils';
import { formatPercentage, getUnitName } from 'shared/src/domain/line-item/line-item-utils';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';
import {
  AgencyMarkup,
  Bold,
  CalculatedField,
  EffectivePrice,
  GrossPrice,
  ReferralFee
} from '@/features/line-items/line-item-form/ui/calculated-fields-components';

type CalculationInput = {
  price: number | undefined;
  media_budget: number | undefined;
  unit_price_type_id: number | undefined;
  planned_unit_cost: number | undefined;
  agency_markup_rate: number | undefined;
  referral_commission_rate: number | undefined;
};

type Props = {
  inputs: CalculationInput;
};

export function MgmtFeeLineItemCalculatedFields({ inputs }: Props) {
  const { price, planned_unit_cost, agency_markup_rate, referral_commission_rate } = inputs;

  if (isNil(price) && isNil(planned_unit_cost)) return null;

  return (
    <div className="flex flex-col gap-2">
      <GrossPrice price={price} agency_markup_rate={agency_markup_rate} />
      <AgencyMarkup price={price} agency_markup_rate={agency_markup_rate} />
      <EffectivePrice price={price} referral_commission_rate={referral_commission_rate} />
      <ReferralFee price={price} referral_commission_rate={referral_commission_rate} />
      <EstimatedUnits inputs={inputs} />
      <PlannedMargin />
    </div>
  );
}

export function MgmtFeeLineItemMediaTeamCalculatedFields({ inputs }: Props) {
  return (
    <div className="flex flex-col gap-2">
      <PlannedMargin />
      <EstimatedUnits inputs={inputs} />
      <MediaBudget media_budget={inputs.media_budget} />
    </div>
  );
}

function EstimatedUnits({ inputs }: { inputs: CalculationInput }) {
  const { unitPriceTypes } = useChannelRatesContext();
  const { unit_price_type_id, media_budget, planned_unit_cost } = inputs;
  const unit_price_type = unitPriceTypes.find(type => type.id === unit_price_type_id);
  const unitLabel = unit_price_type ? getUnitName(unit_price_type.name).full : undefined;

  const estimated_units = calcEstimatedUnits(
    media_budget,
    undoCPM(planned_unit_cost, unit_price_type)
  );

  if (
    isNil(estimated_units) ||
    isNil(media_budget) ||
    isNil(planned_unit_cost) ||
    isNil(unit_price_type)
  )
    return null;

  const unitType = unit_price_type_id === 1 ? ' × 1,000 [CPM]' : '';

  return (
    <CalculatedField
      label="Estimated Units"
      detail={
        <div className="flex items-center gap-2">
          <Bold>{estimated_units.toLocaleString()}</Bold>
          <span className="text-xs capitalize text-gray-600">{unitLabel}</span>
        </div>
      }
      tooltip={
        <>
          Estimated Units is calculated as (
          <span className="font-bold">{formatCurrency(media_budget)}</span> Media Budget /{' '}
          <span className="font-bold">{formatCurrency(planned_unit_cost)}</span> Planned Unit Cost)
          {unitType} for Management Fee line items
        </>
      }
    />
  );
}

function PlannedMargin() {
  return (
    <CalculatedField
      label="Planned Margin"
      detail={formatPercentage(1)}
      tooltip={
        <>
          Planned Margin is <span className="font-bold">N/A</span> for Management Fee line items.
          However, the line item’s fee will factor into the Campaign’s Planned Margin.
        </>
      }
    />
  );
}

type MediaBudgetProps = {
  media_budget: number | undefined;
};

function MediaBudget({ media_budget }: MediaBudgetProps) {
  if (isNil(media_budget)) return null;

  return (
    <CalculatedField
      label="Media Budget"
      detail={formatCurrency(media_budget)}
      tooltip={
        <>
          Media Budget is <span className="font-bold">entered above</span> for Management Fee line
          items
        </>
      }
    />
  );
}
